import React from 'react';
import { Avatar, rem } from '@mantine/core';
import { SpotlightActionData, SpotlightActionGroupData } from '@mantine/spotlight';
import {
  IconSearch,
  IconUser,
  IconUsers,
  IconCalendarEventFilled,
  IconMapPin,
  IconBuilding,
  IconFileText,
  IconListTree,
  IconPalette,
  IconBook2,
  IconHistoryOff,
  IconMusic,
  IconVideo,
  IconSchool,
  IconBuildingStore,
  IconShirt,
  IconHelp,
} from '@tabler/icons-react';
import type { RecentlyViewedItem } from '../../../src/utils/recentlyViewed';

// Types
export interface SearchResult {
  id: string;
  type:
    | 'artist'
    | 'partnership'
    | 'organizer'
    | 'event'
    | 'upcoming_event'
    | 'past_event'
    | 'dance_style'
    | 'event_type'
    | 'post'
    | 'area'
    | 'venue'
    | 'glossary_term'
    | 'song'
    | 'video'
    | 'course'
    | 'store'
    | 'product'
    | 'help_article';
  title: string;
  description: string;
  url: string;
  avatar_url?: string; // Artist avatars, partnership photos or organization images
}

export interface SearchResponse {
  results: SearchResult[];
  total_count: number;
  query: string;
}

// Constants
export const SEARCH_DEBOUNCE_TIME = 300;
export const SPOTLIGHT_RESULTS_LIMIT = 6;
// Events that only matched by city or type sort last server-side, so without a
// reservation dance styles, cities and artists fill every slot.
export const SPOTLIGHT_RESERVED_EVENT_SLOTS = 2;
export const SEARCH_API_ENDPOINT = '/api/search';
export const SEARCH_EVENTS_ENDPOINT = '/api/search_events';

export const SEARCH_RESULT_GROUPS = {
  DANCE_STYLES: 'Dance Styles',
  CITIES: 'Cities',
  VENUES: 'Venues',
  ARTISTS: 'Artists',
  PARTNERSHIPS: 'Partnerships',
  DICTIONARY: 'Dictionary',
  EVENT_TYPES: 'Event Types',
  ORGANIZERS: 'Organizers',
  SONGS: 'Songs',
  VIDEOS: 'Videos',
  POSTS: 'Posts',
  COURSES: 'Courses',
  MERCH: 'Merch',
  HELP: 'Help',
  EVENTS: 'Events',
  OTHER: 'Other',
} as const;

// Icon generation utilities
export const getIconForType = (type: SearchResult['type'], size: number = 24) => {
  const iconStyle = { width: rem(size), height: rem(size) };
  const iconColor = '#EA580C';

  switch (type) {
    case 'dance_style':
      return <IconPalette style={iconStyle} color={iconColor} />;
    case 'area':
      return <IconMapPin style={iconStyle} color={iconColor} />;
    case 'venue':
      return <IconBuilding style={iconStyle} color={iconColor} />;
    case 'artist':
      return <IconUser style={iconStyle} color={iconColor} />;
    case 'partnership':
      return <IconUsers style={iconStyle} color={iconColor} />;
    case 'organizer':
      return <IconListTree style={iconStyle} color={iconColor} />;
    case 'event_type':
      return <IconCalendarEventFilled style={iconStyle} color={iconColor} />;
    case 'glossary_term':
      return <IconBook2 style={iconStyle} color={iconColor} />;
    case 'post':
      return <IconFileText style={iconStyle} color={iconColor} />;
    case 'song':
      return <IconMusic style={iconStyle} color={iconColor} />;
    case 'video':
      return <IconVideo style={iconStyle} color={iconColor} />;
    case 'course':
      return <IconSchool style={iconStyle} color={iconColor} />;
    case 'store':
      return <IconBuildingStore style={iconStyle} color={iconColor} />;
    case 'product':
      return <IconShirt style={iconStyle} color={iconColor} />;
    case 'help_article':
      return <IconHelp style={iconStyle} color={iconColor} />;
    case 'event':
    case 'upcoming_event':
    case 'past_event':
      return <IconCalendarEventFilled style={iconStyle} color={iconColor} />;
    default:
      return <IconSearch style={iconStyle} color={iconColor} />;
  }
};

// Group name mapping
export const getGroupName = (type: SearchResult['type']): string => {
  switch (type) {
    case 'dance_style':
      return SEARCH_RESULT_GROUPS.DANCE_STYLES;
    case 'area':
      return SEARCH_RESULT_GROUPS.CITIES;
    case 'venue':
      return SEARCH_RESULT_GROUPS.VENUES;
    case 'artist':
      return SEARCH_RESULT_GROUPS.ARTISTS;
    case 'partnership':
      return SEARCH_RESULT_GROUPS.PARTNERSHIPS;
    case 'event_type':
      return SEARCH_RESULT_GROUPS.EVENT_TYPES;
    case 'organizer':
      return SEARCH_RESULT_GROUPS.ORGANIZERS;
    case 'glossary_term':
      return SEARCH_RESULT_GROUPS.DICTIONARY;
    case 'post':
      return SEARCH_RESULT_GROUPS.POSTS;
    case 'song':
      return SEARCH_RESULT_GROUPS.SONGS;
    case 'video':
      return SEARCH_RESULT_GROUPS.VIDEOS;
    case 'course':
      return SEARCH_RESULT_GROUPS.COURSES;
    case 'store':
    case 'product':
      return SEARCH_RESULT_GROUPS.MERCH;
    case 'help_article':
      return SEARCH_RESULT_GROUPS.HELP;
    case 'event':
    case 'upcoming_event':
    case 'past_event':
      return SEARCH_RESULT_GROUPS.EVENTS;
    default:
      return SEARCH_RESULT_GROUPS.OTHER;
  }
};

// Convert search results to Spotlight actions
// Pick the results shown in the spotlight: the top results in server order,
// holding back up to SPOTLIGHT_RESERVED_EVENT_SLOTS for upcoming events.
// Unused reserved slots fall back to the next results in line.
export const selectSpotlightResults = (results: SearchResult[]): SearchResult[] => {
  const upcoming = results
    .filter((result) => result.type === 'upcoming_event')
    .slice(0, SPOTLIGHT_RESERVED_EVENT_SLOTS);
  const rest = results.filter((result) => !upcoming.includes(result));
  const top = rest.slice(0, SPOTLIGHT_RESULTS_LIMIT - upcoming.length);
  const leftover = rest.slice(top.length);
  const picked = new Set([...top, ...upcoming, ...leftover].slice(0, SPOTLIGHT_RESULTS_LIMIT));

  return results.filter((result) => picked.has(result));
};

export const convertToSpotlightActions = (
  results: SearchResult[],
  onSelect?: (result: SearchResult) => void,
): SpotlightActionData[] => {
  return selectSpotlightResults(results).map((result) => ({
    id: result.id,
    label: result.title,
    description: result.type === 'upcoming_event' ? `🟠 ${result.description}` : result.description,
    onClick: () => {
      onSelect?.(result);
      window.location.href = result.url;
    },
    leftSection: getSearchResultIcon(result, 32), // Smaller icon size for sleeker look
    group: getGroupName(result.type),
  }));
};

// Spotlight filter: the server already filtered (including synonyms like
// "wcs" -> West Coast Swing), so keep every action. Mantine's default filter
// would drop those hits, but it's also what builds the labeled groups, so do
// that part here: group by `group` label in first-seen order.
// What Spotlight's `actions` / `filter` take; Mantine doesn't export this union.
type SpotlightItem = SpotlightActionData | SpotlightActionGroupData;

export const groupSpotlightActions = (actions: SpotlightItem[]): SpotlightItem[] => {
  const flat = actions.flatMap((a) =>
    'actions' in a ? a.actions.map((inner: SpotlightActionData) => ({ ...inner, group: a.group })) : [a],
  );
  const groups = new Map<string, SpotlightActionGroupData>();
  const result: SpotlightItem[] = [];

  flat.forEach((action) => {
    if (!action.group) {
      result.push(action);
      return;
    }
    let group = groups.get(action.group);
    if (!group) {
      group = { group: action.group, actions: [] };
      groups.set(action.group, group);
      result.push(group);
    }
    group.actions.push(action);
  });
  return result;
};

// Get icon for search result (handles avatars for artists, partnerships and organizers)
export const getSearchResultIcon = (result: SearchResult, size: number = 48) => {
  if ((result.type === 'artist' || result.type === 'partnership' || result.type === 'organizer') && result.avatar_url) {
    return <Avatar src={result.avatar_url} size={size} radius="xl" />;
  }
  return getIconForType(result.type, size);
};

// Spotlight empty state (#1504): the viewer's recently opened pages, most
// recent first, skipping the page they're on, plus a way to clear the list.
export const RECENTLY_VIEWED_GROUP = 'Recently Viewed';
export const RECENTLY_VIEWED_SHOWN = 5;

const RECENTLY_VIEWED_LABELS: Record<RecentlyViewedItem['type'], string> = {
  event: 'Event',
  artist: 'Artist',
  area: 'City',
  dance_style: 'Dance Style',
  post: 'Post',
  organizer: 'Organizer',
  venue: 'Venue',
};

export const createRecentlyViewedActions = (
  items: RecentlyViewedItem[],
  currentPath: string,
  onClear: () => void,
): SpotlightActionData[] => {
  const shown = items.filter((item) => item.url !== currentPath).slice(0, RECENTLY_VIEWED_SHOWN);
  if (shown.length === 0) return [];

  const iconStyle = { width: rem(18), height: rem(18) };
  return [
    ...shown.map((item) => ({
      id: `recent-${item.type}-${item.slug}`,
      label: item.title,
      description: RECENTLY_VIEWED_LABELS[item.type],
      onClick: () => (window.location.href = item.url),
      leftSection: getSearchResultIcon(
        {
          id: item.slug,
          type: item.type,
          title: item.title,
          description: '',
          url: item.url,
          avatar_url: item.imageUrl ?? undefined,
        },
        32,
      ),
      group: RECENTLY_VIEWED_GROUP,
    })),
    {
      id: 'recent-clear',
      label: 'Clear recently viewed',
      onClick: onClear,
      leftSection: <IconHistoryOff style={iconStyle} color="#999999" />,
      group: RECENTLY_VIEWED_GROUP,
    },
  ];
};

// Create "See all results" action
export const createSeeAllResultsAction = (query: string, onShowAll: () => void): SpotlightActionData => ({
  id: 'see-all-results',
  label: `See all results for "${query}"`,
  description: 'View complete search results',
  onClick: onShowAll,
  leftSection: getIconForType('post', 18), // Smaller icon size for sleeker look
  group: 'More Results',
});

// Filter and group results for display
export interface GroupedResults {
  [groupName: string]: SearchResult[];
}

export const groupResultsByType = (results: SearchResult[]): GroupedResults => {
  const grouped: GroupedResults = {};

  results.forEach((result) => {
    const groupName = getGroupName(result.type);
    if (!grouped[groupName]) {
      grouped[groupName] = [];
    }
    grouped[groupName].push(result);
  });

  return grouped;
};

// Groups in the order the server ranked them (#1841), matching the spotlight.
// The server leads with name-matched events, so a fixed group list here would
// undo that. groupResultsByType fills keys in result order.
export const getOrderedGroups = (
  groupedResults: GroupedResults,
): Array<{
  name: string;
  results: SearchResult[];
}> => {
  return Object.entries(groupedResults).map(([name, results]) => ({ name, results }));
};

// Color helpers for event types
export const getEventDescriptionColor = (type: SearchResult['type']): string => {
  return type === 'upcoming_event' ? '#EA580C50' : 'dimmed';
};

// API utility
export interface PopularDanceStyle {
  name: string;
  slug: string;
  upcoming_events_count: number;
}

// Styles offered before anything is typed. Empty on failure: the panel
// still works without them.
export const fetchSearchSuggestions = async (): Promise<PopularDanceStyle[]> => {
  try {
    const response = await fetch(`${SEARCH_API_ENDPOINT}/suggestions`, { headers: { Accept: 'application/json' } });
    if (!response.ok) return [];
    const data = await response.json();
    return data.dance_styles ?? [];
  } catch {
    return [];
  }
};

export const createDanceStyleSuggestionActions = (styles: PopularDanceStyle[]): SpotlightActionData[] =>
  styles.map((style) => ({
    id: `suggested-style-${style.slug}`,
    label: style.name,
    description:
      style.upcoming_events_count > 0
        ? `${style.upcoming_events_count} upcoming ${style.upcoming_events_count === 1 ? 'event' : 'events'}`
        : 'Dance style calendar',
    onClick: () => (window.location.href = `/dance_styles/${style.slug}`),
    leftSection: getIconForType('dance_style'),
    group: SEARCH_RESULT_GROUPS.DANCE_STYLES,
  }));

export const searchAPI = async (query: string): Promise<SearchResponse> => {
  try {
    const response = await fetch(`${SEARCH_API_ENDPOINT}?q=${encodeURIComponent(query)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
      },
    });

    if (!response.ok) {
      throw new Error(`Search failed: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Search error:', error);
    return { results: [], total_count: 0, query };
  }
};

// Search analytics (#1502): report the final query once, with the clicked
// result if there was one. keepalive lets the request finish while the page
// navigates away. Failures are ignored; analytics must never block search.
export interface SearchEventPayload {
  query: string;
  result_count: number | null;
  clicked_result_type?: SearchResult['type'];
  clicked_result_id?: string;
}

export const reportSearchEvent = (payload: SearchEventPayload): void => {
  fetch(SEARCH_EVENTS_ENDPOINT, {
    method: 'POST',
    keepalive: true,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
    },
    body: JSON.stringify(payload),
  }).catch(() => undefined);
};

// Validation helpers
export const isValidSearchQuery = (query: string): boolean => {
  return query.trim().length > 0;
};

export const shouldShowSeeAllAction = (query: string, hasResults: boolean): boolean => {
  return isValidSearchQuery(query) && hasResults;
};
